const basePath = process.env.REACT_APP_REST_ENDPOINT || "";
const neioBasePath = process.env.REACT_APP_NEIO_REST_ENDPOINT || "";
// Auth is handled through iapp-api's /neio/auth-token endpoint, not direct NEIO auth
const neioAuthBasePath = process.env.REACT_APP_NEIO_AUTH_ENDPOINT || "";

type ApiConfig = {
  baseURL: string;
  neioBaseURL: string;
  neioAuthBaseURL: string;
  headers: {
    "Content-Type": string;
  };
  paths: Record<string, string>;
  errorCode: number[];
  successCode: number[];
  excludeToken: string[];
};

const apiConfig: ApiConfig = {
  baseURL: basePath,
  neioBaseURL: neioBasePath,
  neioAuthBaseURL: neioAuthBasePath,
  headers: {
    "Content-Type": "application/json",
  },
  paths: {
    signup: "/auth/signup",
    signin: "/auth/signin",
    checkSession: "/auth/check-session",
    checkCredentials: "/auth/check-credentials",
    getUserProfile: "/auth/profile",
    verifyToken: "/auth/verifyToken",
    refreshToken: "/auth/refresh",
    contactProfile: "/contact",
    kpiContact: "/kpi-proposals/contact",
    reviewContact: "/proposals/contact",
    buySellReviewContact: "/bs-proposals/contact",
    createBuySellProposalInfo: "/bs-proposals-v1/contact",
    proposalPendingbasicInfoUploadDocument: "/proposals-pending/upload/proposal-document",
    bsProposalPendingbasicInfoUploadDocument: "/bs-proposals-pending/upload/proposal-document",
    proposalPendingAdditionReqUploadDocument: "/proposals-pending/upload",
    proposalPendingUploadExtraDocuments: "/proposals-pending/upload/extra-proposal-documents",
    proposalPendingbasicInfoDeleteDocument: "/proposals-pending/proposal-document",
    bsProposalPendingbasicInfoDeleteDocument: "/bs-proposals-pending/proposal-document",
    proposalPendingExtraDocumentName: "/proposals-pending/extra-document/name",
    kpiDeleteDocument: "/kpi-proposals-pending/proposal-document",
    proposalPendingFinalDeleteDocument: "/proposals-pending/signed-proposal",
    bsCreatePlan: "/bs-proposals-pending/create-plan",
    bsCreatePlanV1: "/v1/bs-proposals-pending-v1/create-plan",
    kpiCreatePlan: "/kpi-proposals-pending/create-plan",
    reviewContactTimeline: "/proposals/proposal-logs",
    reviewDuplicateProposalID: "/proposals/duplicate-proposal",
    bsReviewDuplicateProposalID: "/bs-proposals/duplicate-proposal",
    contactDraft: "/contact/list/drafts",
    contactPolicies: "/contact/policies",
    contactProposalsForEntityPending: "/v1/proposals/list-proposals/entity-pending",
    contactProposalsForEntityProposals: "/v1/proposals/list-proposals/entity-proposals",
    interactiveReviewCenterPerformance: "/v1/interactive-review-center/performance",
    blockToken: "/auth/block/otp",
    blockVerifyToken: "/auth/block/otp/verify",
    resetEmail: "/auth/reset/email",
    resetPhone: "/auth/reset/mobile",
    resetEmailToken: "/auth/reset/email/token",
    resetPhoneToken: "/auth/reset/mobile/token",
    contactHealth: "/contact/health",
    resendResetPassword: "/auth/resetPasswordLink",
    resetPasswordEmail: "/auth/reset/email/createpassword",
    resetPasswordPhone: "/auth/reset/mobile/createpassword",
    contactBlock: "/contact/block",
    plansList: "/v1/stripe/plans",
    planChange: "/v1/stripe/create-checkout-session",
    paymentUpdateStatus: "/v1/stripe/update-payment-status",
    inviteEmail: "/invitations",
    inviteEmailAssistant: "/invitations/assistant",
    inviteAgency: "/agency",
    resendInvite: "/invitations/resendInvite",
    getUsers: "/users/profile-names",
    findAdmins: "/users/admin",
    profileCreate: "/profile/create",
    logout: "/auth/logout",
    profileOTP: "/auth/verify/mobile/token",
    agentBrandingUpload: "/upload",
    agencyBrandingSave: "/agency-branding",
    agentBrandingSave: "/agent-branding",
    allBrandingDetails: "/agent-branding/listAllBrandings",
    activeBranding: "/agents/setCurrentBranding",
    activeAgencyBranding: "/agency/setCurrentBranding",
    deleteAgent: "/agents",
    agents: "/agents/onboarded",
    onboardedAssistants: "/agents/onboarded-assistants",
    pendingAgents: "/agents/pending",
    agencies: "/agency/list",
    allAgencies: "/agents/all-agencies",
    updateAgency: "/agents/update-agency",
    updateAgencyAdvisorMapping: "/agency/advisor/update",
    requestProposal: "/v1/proposals/request-proposal",
    requestProposals: "/v1/proposals/list-proposals/requested",
    requestSplitDollarProposalV1: "/v1/sd-proposals-v1/request-proposal",
    adminDraft: "/v1/proposals/list-proposals/draft",
    adsList: "/advertisement",
    adsActiveBannerList: "/advertisement/active-banner-list",
    pendingReviews: "/v1/proposals/list-proposals/pending-review",
    pendingRequest: "/v1/proposals/list-proposals/received",
    proposalsRequestedInactiveReceived: "/proposals/list-proposals/requested-inactive-received",
    subDomain: "/subdomain/set",
    resetPasswordLink: "/auth/reset/link",
    proposalTypes: "/v1/proposals/proposal-types",
    lookupBefore: "/lookup/before",
    lookupAfter: "/lookup/after",
    lookupAfterAgent: "/lookup/assistant/after",
    contactFinancialInfo: "/contact/finance/create",
    superAdminProfile: "/super-admin/profile",
    subscriberCounts: "/super-admin/subscriber-counts",
    iamControlCounts: "/super-admin/iam-control-counts",
    agentProfile: "/profile/agent",
    agentProfileAddContent: "/profile/agent/content",
    agentProfileAddSocialMedia: "/profile/agent/socialMedia",
    agentPinUnpinSection: "/profile/agent/pin-unpin-section",
    agencyProfile: "/profile/agency",
    superAdminAgents: "/super-admin/agents",
    financialInfo: "/contact/finance",
    addPolicyInput: "/v1/proposals/add-policy-input",
    policyInputCSVData: "/v1/proposals/policy-input",
    proposalBuyNSell: "/bs-proposals/bs-proposal-valuations",
    insuranceCompanies: "/v1/proposals/insurance-companies",
    userProfile: "/profile",
    contact: "/contact",
    contactTransaction: "/contact/transaction",
    contactPersonalInfoEdit: "/contact/profile",
    profilePhoneOtp: "/profile/phone/token",
    profilePhoneVerifyOtp: "/profile/phone/verify",
    agentProfilePicture: "/profile/pictureUpload",
    agentContactPersonalInfoSave: "/contact/profile",
    resetProfileEmail: "/profile/email/token",
    resetProfileEmailToken: "/profile/email/verify",
    /** Validates an email OTP without applying the change — used by the assistant profile edit. */
    profileEmailVerifyOnly: "/profile/email/verify-only",
    /** PATCH /profile/assistant/{userId} — updates an assistant's own details. */
    assistantProfileEdit: "/profile/assistant",
    resetProfilePhone: "/profile/phone/token",
    resetProfilePhoneToken: "/profile/phone/verify",
    contactEmailList: "/contact/list",
    contactEmailToken: "/contact/email/token",
    contactEmailVerify: "/contact/email/verify",
    contactEmailVerifyPrimary: "/contact/email/primary/verify",
    contactEmailDelete: "/contact/email",
    contactEmailPrimary: "/contact/email/primary",
    contactEmailResendOtp: "/contact/email/resend-token",
    contactPhonePrimary: "/contact/phone/primary",
    contactPhoneVerifyPrimary: "/contact/phone/primary/verify",
    contactPhoneResendOtp: "/contact/phone/resend-token",
    contactPhoneToken: "/contact/phone/token",
    contactPhoneVerify: "/contact/phone/verify",
    contactPhoneDelete: "/contact/phone",
    contactAddressCreate: "/contact/address",
    profile: "/profile",
    profilePictureUpload: "/profile/contactProfileUpload",
    profilePictureDelete: "/profile/profilePicture",
    deleteBrandingLogo: "/agent-branding/branding-logo",
    deleteBrandingBannerDarkLogo: "/agent-branding/branding-logo/bannerLogoDark",
    deleteBrandingBannerLogo: "/agent-branding/branding-logo/bannerLogo",
    contactHealthInfo: "/contact/health/create",
    proposalsPlanAdminSplitDollar: "/proposals/plan-admin-split-dollar",
    createProposalContactInfo: "/proposals/contact",
    revokeProposalContact: "/proposals/action/revoked",
    revokeProposal: "/proposals/action/pending-revoked",
    pdfReport: "/proposals/pdf-report",
    createProposal: "/proposals/create-proposal",
    sendProposal: "/proposals/send-proposal",
    reqSign: "/proposals/contact/req-sign",
    proposalsInsuranceCompanies: "/v1/proposals/insurance-companies",
    proposalActionReview: "/proposals/action/reviewed",
    planSplitDollar: "/proposals/plan-split-dollar",
    commission: "/commission",
    sendCommission: "/commission/create",
    deleteCommission: "/commission/delete",
    saveProposalPlan: "/proposals/plan-data-save",
    loanPlanProjection: "/proposals/plan-projection",
    createProposalOtherDetails: "/proposals/other-details",
    contactProposals: "/v1/proposals/list-proposals/contact",
    proposalTimeline: "/proposals/proposal-logs",
    proposalPdf: "/proposals/report",
    proposalPendingPdf: "/proposals-pending/report",
    proposalChanges: "/proposals/change-request",
    pendingApprove: "/v1/proposals/list-proposals/pending-approve",
    IsProgressApprove: "/proposals/list-proposals/pending-in-progress",
    pendingProposal: "/v1/proposals/list-proposals/approved-pending",
    pendingDocumentListView: "/proposals-pending/list-proposal-document",
    uploadSignedReport: "/proposals/upload/signed-report",
    requestNewSignature: "/proposals/action/req-new-signature",
    approveProposal: "/proposals/action/approved",
    approveProposalPending: "/kpi-proposals-pending/plan-finalization/approve-original-proposal",
    helloSignUrl: "/proposals/hello-sign-report",
    checkHelloSignStatus: "/proposals/hello-sign-report-status",
    proposalPendingBasicInfo: "/proposals-pending/basic-info",
    bsproposalPendingBasicInfo: "/bs-proposals-pending/basic-info",
    kpiProposalPendingBasicInfo: "/kpi-proposals-pending/basic-info",
    pfProposalPendingBasicInfo: "/pf-proposals-pending/basic-info",
    pfProposalInvoiceFunding: "/pf-proposals-pending/invoices-list",
    pfProposalInsuredsList: "/pf-proposals-pending/insureds-list",
    proposalPendingHelloSign: "/proposals-pending/hello-sign-report",
    proposalPendingHelloSignStatus: "/proposals-pending/hello-sign-report-status",
    proposalPendingInsuranceUnderwriting: "/proposals-pending/insurance-underwriting",
    proposalPendingPlanFinalize: "/proposals-pending/plan-finalization",
    proposalPendingSaveDetails: "/proposals-pending/save-details",
    bsProposalPendingSaveDetails: "/bs-proposals-pending/save-details",
    kpiProposalPendingSaveDetails: "/kpi-proposals-pending/save-details",
    proposalPendingApproveDoc: "/proposals-pending/approve",
    proposalPendingRejectDoc: "/proposals-pending/reject",
    proposalPendingDocLink: "/proposals-pending/doc-link",
    inactiveProposals: "/proposals/list-proposals/inactive",
    inactivePending: "/proposals/list-proposals/pending-inactive",
    plansGraphViews: "/plans/details",
    plansGraphViewsActive: "/plans/details/active",
    policyGraph: "/plans/policy-graph",
    policyGraphByProduct: "/plans/policy-graph-by-product",
    plansAnalyticsGraph: "/plans/analytics-graph",
    plansPlansAddedGraph: "/plans/plans-added-graph",
    plansPoliciesAddedGraph: "/plans/policies-added-graph",
    plansFinancingsAddedGraph: "/plans/financings-added-graph",
    upcomingPlanReviewStats: "/plans/upcoming-plan-review-stats",
    upcomingPlanReviewList: "/plans/upcoming-plan-review-list",
    upcomingFinancingReviewStats: "/plans/upcoming-financing-review-stats",
    upcomingFinancingReviewList: "/plans/upcoming-financing-review-list",
    plansTableView: "/plans/list/all",
    loansTableView: "/plans/loan/list/all",
    pastDues30Days: "/plans/list-upcoming-reviews/past-dues-30-days",
    pastDues: "/plans/list-upcoming-reviews/past-dues",
    next30Days: "/plans/list-upcoming-reviews/next-30-days",
    next45Days: "/plans/list-upcoming-reviews/next-45-days",
    deleteAdditionalReqTask: "/proposals-pending/additional-document",
    planFinalization: "/proposals-pending/plan-finalization",
    kpiPlanFinalization: "/kpi-proposals-pending/plan-finalization",
    pfPlanFinalization: "/pf-proposals-pending/finalization",
    pfChangeRequestFinalization: "/pf-proposals-pending/change-request",
    updateProposal: "/proposals-pending/plan-finalization/proposal/updated",
    billingList: "/billing/list/all",
    billingInvoice: "/billing/invoice",
    conciergeService: "/subscription/conciergeService",
    viewPolicyDetails: "/plans/policy-review",
    viewPlanDetails: "/plans/plan-review",
    summaryGraph: "/plans/plan-graph",
    planSummary: "/plans/plan-summary",
    loanSummary: "/plans/loan-summary",
    policyEffectiveDate: "/plans/policy-effective-date",
    bsPolicyShareholders: "/bs-plans/policy/shareholders",
    kpiPolicyEmployees: "/kpi-plans/policy/employees",
    bsPolicySummary: "/bs-plans/policy-summary",
    kpiPolicySummary: "/kpi-plans/policy-summary",
    commissionRequest: "/commission/requests",
    insuranceCompany: "/insurance-company",
    insuranceProduct: "/insurance-product",
    cancelSubscription: "/subscription/cancel",
    reportsInsuranceCompany: "/reports/insurance-company",
    reportsIappCommissions: "/reports/iapp-commissions",
    reportsAgentCommissions: "/reports/agent-commission",
    commissionApproval: "/commission/approval",
    reportsAgencyCommissions: "/reports/agency-commission",
    verifyStripeAccount: "/insurance-company/stripe-account/details",
    saveStripeAccountKey: "/insurance-company/stripe-account",
    deleteStripeAccountKey: "/insurance-company/stripe-account",
    insuranceCompanyBranding: "/insurance-company/branding",
    viewCommissionUser: "/commission/user",
    planEffectiveDate: "/plans/plan-effective-date",
    loanReview: "/plans/loan-review",
    agencyCommissionReports: "/reports/agency-commission",
    afrDetails: "/super-admin/master-data/afr",
    provisionDetails: "/super-admin/master-data/provisions",
    requestBuySellProposal: "/bs-proposals/request-proposal",
    requestBuySellProposalV2: "/bs-proposals-v1/request-proposal",
    requestKeyPersonProposal: "/kpi-proposals/request-proposal",
    requestKeyPersonProposalV1: "/kpi-proposals-v1/request-proposal",
    requestPremiumFinanceProposal: "/pf-proposals/request-proposal",
    createBuySellProposalContactInfo: "/bs-proposals/contact",
    buySellProposalMasterDataProvisions: "/super-admin/master-data/provisions",
    buySellProposalProvisions: "/bs-proposals/provisions",
    addBuySellProposalProvisions: "/bs-proposals/provisions",
    fetchProposedPolicyLedger: "/bs-proposals/life-policy-ledger",
    bsProposedLifePolicyLedgerV1: "/bs-proposals-v1/life-policy-ledger",
    fetchProposedDisabilityPolicyLedger: "/bs-proposals/disability-policy-ledger",
    bsProposedDisabilityPolicyLedgerV1: "/bs-proposals-v1/disability-policy-ledger",
    buySellProposalPendingBusinessValuation: "/bs-proposals-pending/business-valuation",
    buySellProposalPendingApproveDoc: "/bs-proposals-pending/approve",
    buySellProposalPendingRejectDoc: "/bs-proposals-pending/reject",
    kpiProposalPendingApproveDoc: "/kpi-proposals-pending/approve",
    kpiProposalPendingRejectDoc: "/kpi-proposals-pending/reject",
    buySellProposalPendingBusinessValuationUploadDocument:
      "/bs-proposals-pending/upload/proposal-document",
    kpiProposalPendingBusinessValuationUploadDocument:
      "/kpi-proposals-pending/upload/proposal-document",
    bsProposalsPendingBasicInfoGenerateInvoice: "/bs-proposals-pending/basic-info/generate-invoice",
    kpiProposalsPendingBasicInfoGenerateInvoice:
      "/kpi-proposals-pending/basic-info/generate-invoice",
    proposalsPendingBasicInfoGenerateInvoice: "/proposals-pending/basic-info/generate-invoice",
    buySellProposalPendingBusinessValuationDeleteDocument:
      "/bs-proposals-pending/proposal-document",
    shareHolderLedgerFetchOperation: "/bs-proposals/shareholder-ledger",
    shareHolderLedgerFetchOperationplan: "/bs-plans/shareholder-ledger",
    kpiLedgerFetchOperationplan: "/kpi-plans/employee-ledger",
    kpiEmployeeLedgerPlanDetails: "/kpi-plans/employee-ledger",
    shareHolderLedgerOperations: "/bs-proposals/shareholder",
    shareHolderLedgerList: "/bs-proposals/shareholders",
    kpishareHolderLedgerList: "/kpi-proposals/employees",
    requestProposalProgress: "/bs-proposals/progress",
    disabilityInsurance:
      "/bs-proposals-pending/disability-insurance-underwriting/{proposalId}/{pendingId}",
    kpidisabilityInsurance:
      "/kpi-proposals-pending/disability-insurance-underwriting/{proposalId}/{pendingId}",
    disabilityInsuranceSave: "/bs-proposals-pending/save-details",
    disabilityInsuranceShareholder:
      "/bs-proposals-pending/disability-insurance-underwriting/shareholders",
    kpidisabilityInsuranceShareholder:
      "/kpi-proposals-pending/disability-insurance-underwriting/employees",
    disabilityInsuranceShareholderPlan: "/bs-plans/disability-insurance-underwriting/shareholders",
    disabilityInsuranceShareholderSave:
      "/bs-proposals-pending/disability-insurance-underwriting/shareholders/{proposalId}",
    kpiDisabilityInsuranceShareholderSave:
      "/kpi-proposals-pending/disability-insurance-underwriting/employee",
    disabilityInsuranceInvoiceGenerate:
      "/bs-proposals-pending/disability-insurance-underwriting/generate-invoice",
    lifeInsuranceUnderwriting: "/bs-proposals-pending/life-insurance-underwriting/shareholders",
    kpilifeInsuranceUnderwriting: "/kpi-proposals-pending/life-insurance-underwriting/employees",
    lifeInsuranceUnderwritingPlan: "/bs-plans/life-insurance-underwriting/shareholders",
    lifeInsuranceUnderwritingPost:
      "/bs-proposals-pending/life-insurance-underwriting/shareholders/{proposalId}",
    kpiLifeInsuranceUnderwritingPost: "/kpi-proposals-pending/life-insurance-underwriting/employee",
    lifeInsurancePending:
      "/bs-proposals-pending/life-insurance-underwriting/{proposalId}/{pendingId}",
    kpilifeInsurancePending:
      "/kpi-proposals-pending/life-insurance-underwriting/{proposalId}/{pendingId}",
    lifeInsuranceInvoiceGeneration:
      "/bs-proposals-pending/life-insurance-underwriting/generate-invoice",
    lifeInsuranceuploadDoc: "/bs-proposals-pending/upload/proposal-document",
    lifeInsuranceSaveDetails: "/bs-proposals-pending/save-details",
    buySellproposalPendingPlanFinalize: "/bs-proposals-pending/plan-finalization",
    buySellproposalPendingSaveDetails: "/bs-proposals-pending/save-details",
    buySellproposalPendingbasicInfoUploadDocument: "/bs-proposals-pending/upload/proposal-document",
    buySellcreateProposal: "/bs-proposals/create-proposal",
    buySellsendProposal: "/bs-proposals/send-proposal",
    buySellCreatePlan: "/bs-proposals-pending/create-plan",
    buySellplanSummary: "/bs-plans/plan-summary",
    buySellPlanSummaryV1: "/v1/bs-plans-v1/plan-summary",
    buySellPlanInfoUpdate: "/v1/bs-plans-v1/plan-info",
    kpiPlanSummary: "/kpi-plans/plan-summary",
    kpiPlanSummaryV1: "/v1/kpi-plans-v1/plan-summary",
    kpiPlanInfoUpdate: "/v1/kpi-plans-v1/plan-info",
    kpiInvoiceNotifications: "/v1/kpi-plans-v1/invoice-notifications",
    bsInvoiceNotifications: "/v1/bs-plans-v1/invoice-notifications",
    kpiproposalPendingUploadDocument: "/kpi-proposals-pending/upload/proposal-document",
    addtionalRequestPlanSummary: "/bs-plans/review/additional-benefits",
    valuationAndContributionReview: "/bs-plans/valuation-contributions-review",
    kpiPlanReview: "/kpi-plans/plan-review",
    triggeringEvents: "/bs-plans/triggering-events",
    shareHoldersEventTypes: "/bs-plans/triggering-events/shareholders-event-types",
    requestAddtionalRequestPlanSummary: "/bs-plans/additional-benefits",
    transectionLedger: "/bs-plans/transaction-ledger",
    policyTransectionLedger: "/bs-plans/policy-transaction-ledger",
    lifePolicyLedgerPlan: "/bs-plans/life-policy-ledger",
    lifePolicyLedgerPlanPost: "/bs-plans/life-policy-ledger",
    kpilifePolicyLedgerPlan: "/kpi-plans/life-policy-ledger",
    kpilifePolicyLedgerPlanPost: "/kpi-plans/life-policy-ledger",
    disabilityLedgerPlan: "/bs-plans/disability-policy-ledger",
    kpidisabilityLedgerPlan: "/kpi-plans/disability-policy-ledger",
    transactionLedgerEndpoint: "/bs-plans/transaction-ledger",
    settingsProfile: "/profile/phone/otp",
    settingsVerifyOtp: "/profile/otp/verify",
    settingsChangePassword: "/auth/changepassword",
    contactList: "/contact/list/all",
    setLoanAFR: "/plans/set-loan-afr",
    agencyProfileAdmin: "/profile/agency",
    agentListing: "/agents/list-agency-agents",
    agentsAgencyCounts: "/agents/agency-agents-counts",
    brandingList: "/agency/sub-agencies",
    caseManager: "/super-admin/admins",
    inviteAgentFromAgency: "/invitations/agents-in-agency",
    agentsContactAndPlanCount: "/agents/contact-and-plan-count",
    agentProfileId: "/profile",
    superAdminAgentProfileEdit: "/profile/agent",
    adminAgentContactListing: "/contact/lists",
    allContactsListing: "/contact/list/all",
    adminAgentEntityListing: "/entity/list-by-advisor",
    agentsPolicies: "/agents/policies",
    teamListing: "/super-admin/team-members",
    removeTeamMember: "/super-admin/remove-member",
    agentOrgChart: "/agents/org-chart",
    getAdminRoles: "/super-admin/master-data/role",
    postInviteTeamMember: "/invitations/admin",
    superAdminPending: "/super-admin/admins/pending",
    orgChart: "/agency/org-chart",
    organisationRoles: "/super-admin/master-data/role",
    superAdminsList: "/super-admin/admins",
    superAdminSetRole: "/super-admin/set-role",
    saAgencyAdminsList: "/agency/sub-agencies",
    saAssistantsList: "/agency/assistants",
    contactSummary: "/contact/summary",
    updateAgentRole: "/agency/agent-role",
    removeAgentRole: "/agency/remove/agent-role",
    agencyEditProfile: "/agency/profile",
    subAgencyInvite: "/agency/subagency",
    agencySubAgencyStats: "/agency/sub-agency-stats",
    agentAssistant: "/agents/assistants",
    convertToAgent: "/agency/sub-agency-manager-to-agent",
    deleteAssistant: "/agency/assistant",
    deleteAdminAgentAssistant: "/super-admin/assistant",
    agencyManageAssistant: "/agency/manage-assistant",
    updateDeviceToken: "/notifications/update-device-token",
    notifications: "/notifications",
    readNotification: "/notifications/mark-read",
    unreadNotification: "/notifications/mark-unread",
    readAllNotifications: "/notifications/mark-all-read",
    readProposalReceivedNotification: "/notifications/mark-proposal-received-read",
    genrateLoanReviewInvoice: "/plans/generate-loan-review-invoice",
    deleteLoanReviewInvoice: "/plans/loan-review-invoice",
    subscriptionCancel: "/stripe/subscription/cancel",
    policyPastDues30Days: "/plans/policy-upcoming-reviews/past-dues-30-days",
    policyUpcomingReviews: "/plans/upcoming-policy-review-list",
    upcomingPolicyReviewStats: "/plans/upcoming-policy-review-stats",
    policyPastDues: "/plans/policy-upcoming-reviews/past-dues",
    policyNext30Days: "/plans/policy-upcoming-reviews/next-30-days",
    policyNext45Days: "/plans/policy-upcoming-reviews/next-45-days",
    authSwitchUser: "/auth/switch",
    saveThemeSettings: "/profile/setting/theme",
    saveFontSettings: "/profile/setting/fontsize",
    validateInvite: "/invitations/validateInvite",
    generateEmployeeInvoice: "/plans/generate-plan-review-employee-invoice",
    generateEmployerInvoice: "/plans/generate-plan-review-employer-invoice",
    deleteEmployeeInvoice: "/plans/plan-review-employee-invoice",
    deleteEmployerInvoice: "/plans/plan-review-employer-invoice",
    deleteBasicInfoInvoice: "/proposals-pending/basic-info/invoice",
    restoreProposal: "/proposals-pending/plan-finalization/restore-proposal",
    planUpgrade: "/stripe/subscription/upgrade",
    chatUser: "/chat/user",
    chatFileUpload: "/chat/upload/doc",
    chatFile: "/chat/doc",
    chatFileRemove: "/chat/remove/doc",
    proposalStats: "/proposals/stats",
    waiveOffEmployeeInvoice: "/plans/waive-off-late-fee-employee-invoice",
    waiveOffEmployerInvoice: "/plans/waive-off-late-fee-employer-invoice",
    waiveOffLoanReviewInvoice: "/plans/waive-off-late-fee-loan-review-invoice",
    waiveOffBSValuationInvoice: "/bs-plans/waive-off-late-fee",
    kpiWaiveOffBSValuationInvoice: "/kpi-plans/waive-off-late-fee",
    emailNotification: "/profile/setting/emailNotification",
    loanDetail: "/plans/loan/details",
    attentions: "/notifications/attentions",
    policyBenefits: "/plans/policy-benefits",
    cashValues: "/plans/cash-values",
    kpiProposalContactInfo: "/kpi-proposals/contact",
    kpiEmployeeLedger: "/kpi-proposals/employees-ledger",
    kpiProposedLifePolicyLedger: "/kpi-proposals/life-policy-ledger",
    kpiProposedLifePolicyLedgerV1: "/kpi-proposals-v1/life-policy-ledger",
    kpiProposedDisabilityPolicyLedger: "/kpi-proposals/disability-policy-ledger",
    kpiProposedDisabilityPolicyLedgerV1: "/kpi-proposals-v1/disability-policy-ledger",
    kpiProposalValuation: "/kpi-proposals/proposal-valuation",
    kpiProposalEmployee: "/kpi-proposals/employee",
    kpiEmployeesLedgerEmployee: "/kpi-proposals/employee",
    kpiCreateProposal: "/kpi-proposals/create-proposal",
    kpiSendProposal: "/kpi-proposals/send-proposal",
    kpiProgress: "/kpi-proposals/progress",
    kpiDuplicateProposal: "/kpi-proposals/duplicate-proposal",
    insuranceCompanyFileUpload: "/insurance-company/upload/doc",
    insuranceCompanyLogoUpload: "/insurance-company/upload/logo",
    insuranceCompanyDeleteDocument: "/insurance-company/doc",
    insuranceCompanyDeleteLogo: "/insurance-company/logo",
    insuranceCompanySectionDocumentUpload: "/insurance-company",
    addAdvisorToPlan: "/plans/add-advisor",
    removeAdvisorFromPlan: "/plans/remove-advisor",
    addAdvisorToContact: "/contact/add-advisor",
    removeAdvisorFromContact: "/contact/remove-advisor",
    allAgents: "/super-admin/all-agents",
    addAgentSDCommissionTracking: "/plans/commissions/add-agent",
    addAgentBSCommissionTracking: "/bs-plans/commissions/add-agent",
    addAgentBSCommissionTrackingV1: "/v1/bs-plans-v1/commissions/add-agent",
    addAgentKPCommissionTracking: "/kpi-plans/commissions/add-agent",

    addAgentPFCommissionTracking: "/pf-plans/commissions/add-agent",

    getSDCommissionTracking: "/plans/commissions",
    getBSCommissionTracking: "/bs-plans/commissions",
    getKPCommissionTracking: "/kpi-plans/commissions",
    getPFCommissionTracking: "/pf-plans/commissions",
    removeAgentSDCommissionTracking: "/plans/commissions/remove-agent",
    removeAgentBSCommissionTracking: "/bs-plans/commissions/remove-agent",
    removeAgentKPCommissionTracking: "/kpi-plans/commissions/remove-agent",
    removeAgentPFCommissionTracking: "/pf-plans/commissions/remove-agent",
    updateAgentSDCommissionTracking: "/plans/commissions/update-agent",
    updateAgentBSCommissionTracking: "/bs-plans/commissions/update-agent",
    updateAgentKPCommissionTracking: "/kpi-plans/commissions/update-agent",
    updateAgentPFCommissionTracking: "/pf-plans/commissions/update-agent",
    getAgentCommissionSD: "/plans/agent-commissions",
    getAgentCommissionBS: "/bs-plans/agent-commissions",
    getAgentCommissionKP: "/kpi-plans/agent-commissions",
    getAgentCommissionPF: "/pf-plans/agent-commissions",
    getCommisionRates: "/commission/rates",
    getCommissionActivity: "/commission/activity",
    paymentAllocation: "/commission/payment-allocation",
    advisorPaymentAllocation: "/commission/advisor-payment-allocation",
    getInvoicePaymentIntent: "/stripe/invoice",
    createPaymentMethodSetupIntent: "/stripe/payment-method/setup-intent",
    proposalRequestCommentChanges: "/proposals/interactive/request-change",
    proposalComment: "/proposals/interactive/comments",
    proposalResolveAllComments: "/proposals/interactive/resolve-all-comments",
    interactiveProposalSummary: "/proposals/interactive/summary",
    interactiveProposalSummaryInsureds: "/pf-proposals/insureds",
    interactiveProposalDownload: "/proposals/interactive/pdf-data",
    interactiveProposalFunding: "/proposals/interactive/funding",
    interactiveProposalProjection: "/proposals/interactive/projection",
    interactiveProposalSolution: "/proposals/interactive/solution",
    interactiveRevCentSolution: "/v1/interactive-review-center/solution",
    interactiveProposalAppendix: "/proposals/interactive/appendix",
    interactiveProposalAppendixDocumentList: "/proposals/interactive/list-documents",
    interactiveProposalAppendixDocumentUpload: "/proposals/interactive/upload/document",
    interactiveProposalAppendixDocumentDelete: "/proposals/interactive/delete-document",
    interactiveProposalVersionList: "/proposals/interactive/version-list",
    interactiveProposalCreateNewVersion: "/proposals/interactive/create-new-version",
    interactiveProposalApprovalDetails: "/proposals/interactive/approval-details",
    interactiveReviewCenterSummary: "/v1/interactive-review-center/summary",
    interactiveReviewCenterPdfData: "/v1/interactive-review-center/pdf-data",
    trueIQData: "/v1/interactive-review-center/trueiq",
    interactiveReviewCenterFunding: "/v1/interactive-review-center/funding",
    interactiveReviewCenterSignature: "/v1/interactive-review-center/signature",
    interactiveReviewCenterConfirmSignature: "/v1/interactive-review-center/confirm",
    interactiveReviewCenterSkipSignature: "/v1/interactive-review-center/skip",
    interactiveReviewCenterPlanAppendix: "/v1/interactive-review-center/plan-appendix",
    fundingCenter: "/plan-funding-center",
    fundingCenterPending: "/plan-funding-center/pending",
    fundingCenterAmount: "/plan-funding-center/amount",
    fundingCenterExternal: "/plan-funding-center/external",
    fundingCenterTimelineExternal: "/plan-funding-center/timeline",
    fundingCenterAutoPay: "/plan-funding-center",
    fundingCenterInsurerAuth: "/plan-funding-center",
    interactiveProposalSignatureUpload: "/proposals/interactive/upload/signed-proposal",
    interactiveProposalSignatureDelete: "/proposals/interactive/upload/signed-proposal",
    interactiveProposalAdvisorProposalApproval: "/proposals/interactive/advisor/approval",
    interactiveProposalContactProposalApproval: "/proposals/interactive/contact/approval",
    interactiveProposalAdminProposalApproval: "/proposals/interactive/admin/approval",
    interactiveProposalProposalRevoke: "/proposals/interactive/revoke",
    interactiveProposalProposalRequestNewSignature: "/proposals/interactive/request-new-signature",
    updateIncomeStartAge: "/proposals/update-income-start-age",
    getAllCourses: "/courses",
    courseComments: "/course/comments/course",
    commentThread: "/course/comments/thread",
    commentReplies: "/course/comments",
    commentReply: "/course/comments/reply",
    getContinueLearningCourses: "/courses/latest-viewed",
    ipUpdateBranding: "/proposals/interactive/update-branding",
    addCourse: "/courses",
    publishCourse: "/courses/publish",
    getCourseComments: "/course/comments/sublesson",
    createCourseComment: "/course/comments",
    createCourseReply: "/course/comments/reply",
    updateCourseViewStatus: "/courses/update-view-status",
    toggleSubLessonCompletionStatus: "/courses/toggle-sublesson-completion-status",
    getMilestones: "/milestone",
    getFixedBadges: "/milestone/badges",
    swapMilestones: "/milestone/swap-order",
    bonusCredits: "/bonus-credits",
    rewardsCatalogDocument: "/rewards-catalog/document",
    rewardsCatalogRewards: "/rewards-catalog/rewards",
    redemptionQueue: "/redemption-queue",
    redemptionQueueCounts: "/redemption-queue/counts",
    redemptionQueueCreate: "/redemption-queue/create",
    contactPendingApprove: "/proposals-pending/plan-finalization/proposal/approve",
    markBadgeCriteria: "/milestone/mark-badge-criteria",
    markGoalCriteria: "/goals/mark-goal-criteria",
    advertisement: "/advertisement",
    advisorAnalyticsTotals: "/analytics/advisor/totals",
    advisorAnalyticsOutstanding: "/analytics/advisor/outstanding",
    advisorAnalyticsPlanTotals: "/analytics/advisor/plan-totals",
    adminAnalyticsTotals: "/analytics/admin/totals",
    adminAnalyticsOutstanding: "/analytics/admin/outstanding",
    adminAnalyticsPlanTotals: "/analytics/admin/plan-totals",
    advisorAnalyticsPolicyTotals: "/analytics/advisor/policy-totals",
    adminAnalyticsPolicyTotals: "/analytics/admin/policy-totals",
    advisorAnalyticsEarningReportTotals: "/analytics/advisor/earning-report-totals",
    agencyAnalyticsEarningReportTotals: "/analytics/agency/earning-report-totals",
    advisorAnalyticsPremiumReportTotals: "/analytics/advisor/premium-report-totals",
    agencyAnalyticsPremiumReportTotals: "/analytics/agency/premium-report-totals",
    advisorAnalyticsPremiumTrends: "/analytics/advisor/premium-report-graph",
    agencyAnalyticsPremiumTrends: "/analytics/agency/premium-report-graph",
    advisorAnalyticsPremiumTrendsCompare: "/analytics/advisor/premium-report-graph-compare",
    agencyAnalyticsPremiumTrendsCompare: "/analytics/agency/premium-report-graph-compare",
    advisorEarningReportGraph: "/analytics/advisor/earning-report-graph",
    agencyAnalyticsEarningReportGraph: "/analytics/agency/earning-report-graph",
    advisorEarningCompare: "/analytics/advisor/earning-report-graph-compare",
    agencyEarningCompare: "/analytics/agency/earning-report-graph-compare",
    advisorTopCompanies: "/analytics/advisor/companies-progress",
    agencyAnalyticsTotals: "/analytics/agency/totals",
    agencyAnalyticsPlanTotals: "/analytics/agency/plan-totals",
    agencyAnalyticsOutstanding: "/analytics/agency/outstanding",
    agencyAnalyticsPolicyTotals: "/analytics/agency/policy-totals",
    advisorAnalyticsGoals: "/goals",
    resolveAllContactComments: "/proposals/interactive/resolve-comments",
    virtualAdvisors: "/bots",
    listAgencies: "/super-admin/list-agencies",
    listDocPendingTemplates: "/super-admin/master-data/documents",
    masterDataPendingTemplatesUpload: "/super-admin/master-data/documents/upload",
    masterDataPendingTemplatesDelete: "/super-admin/master-data/documents",
    agencyReferrals: "/agency/referrals",
    advisorReferral: "/profile/assignAdvisor",
    inviteAdvisorReferral: "/invitations/invite-agents",
    neioUploadFile: "/api/v1/bots", // Updated to UIP v1 API path
    neioCoreChat: "/v1",
    neioAuthToken: "/neio/auth-token",
    globalSearch: "/users/global/search",
    pfProposals: "/pf-proposals",
    kpiProposalsV1: "/kpi-proposals-v1",
    policyAdminFinancingModel: "/pf-proposals/policy-admin-financing-model",
    updatePolicyAdminFinancingModel: "/pf-proposals/policy-admin-financing-model",
    pfProposalDetails: "/pf-proposals/proposal-details",
    kpiProposalDetailsV1: "/kpi-proposals-v1/proposal-details",
    policyViewFinancingModel: "/pf-proposals/policy-viewable-financing-model",
    addPfPolicyInputBase: "/pf-proposals/policy-input-base",
    pfPolicyInputBase: "/pf-proposals/policy-input-base",
    pfProgress: "/pf-proposals/progress",
    pfPolicyInputStressTest: "/pf-proposals/policy-input-stress-test",
    pfPolicyInputStressData: "/pf-proposals/policy-input-stress-test",
    pfCreateProposal: "/pf-proposals/create-proposal",
    pfSendProposal: "/pf-proposals/send-proposal",
    pfInsuredsWithCompanyProduct: "/pf-proposals/insureds",
    pfInsuredsList: "/pf-proposals-pending/insureds-list",
    pfThreadMentionsSearch: "/threads/mentions/search",
    pfthreads: "/threads",
    pfProposalsPendingBasicInfo: "/pf-proposals-pending/basic-info",
    pfProposalsPendingFinancing: "/pf-proposals-pending/financing",
    pfProposalsPendingSaveDetails: "/pf-proposals-pending/save-details",
    pfProposalsPendingUploadDocument: "/pf-proposals-pending/upload/proposal-document",
    pfProposalsPendingUploadDoc: "/pf-proposals-pending/upload",
    pfProposalsPendingUploadAdditionalReqDocument: "/pf-proposals-pending/upload",
    pfProposalsPendingUpdateAdditionalReq: "/pf-proposals-pending/additional-requirements",
    pfProposalsPendingAdditionalReqApprove: "/pf-proposals-pending/additional-requirements/approve",
    pfProposalsPendingAdditionalReqReject: "/pf-proposals-pending/additional-requirements/reject",
    sdPendingV1BasicInfo: "/v1/sd-pending-v1/basic-info",
    sdPendingV1BasicInfoGenerateInvoice: "/v1/sd-pending-v1/basic-info/generate-invoice",
    sdPendingV1SaveDetails: "/v1/sd-pending-v1/save-details",
    sdPendingV1InsuranceUnderwriting: "/v1/sd-pending-v1/insurance-underwriting",
    sdPendingV1Financing: "/v1/sd-pending-v1/financing",
    sdPendingV1PlanFinalization: "/v1/sd-pending-v1/plan-finalization",
    sdPendingV1CreatePlan: "/v1/sd-pending-v1/create-plan",
    sdPendingV1InsuranceGenerateInvoice:
      "/v1/sd-pending-v1/insurance-underwriting/generate-invoice",
    sdPendingV1FinancingGenerateInvoice: "/v1/sd-pending-v1/financing/generate-invoice",
    sdPendingV1DeleteInvoice: "/v1/sd-pending-v1/invoice",
    sdPendingV1AdditionalReqUpdate: "/v1/sd-pending-v1/additional-requirements",
    sdPendingV1AdditionalReqApprove: "/v1/sd-pending-v1/additional-requirements/approve",
    sdPendingV1AdditionalReqReject: "/v1/sd-pending-v1/additional-requirements/reject",
    sdPendingV1UploadAdditionalReqDocument: "/v1/sd-pending-v1/upload",
    sdPendingV1UploadDocument: "/v1/sd-pending-v1/upload/proposal-document",
    sdPendingV1InvoicesList: "/v1/sd-pending-v1/invoices-list",
    sdPendingV1ChangeRequest: "/v1/sd-pending-v1/change-request",
    sdPendingV1InsuredEntities: "/v1/sd-pending-v1/insured-entities",
    sdPlanCommissionsV1: "/v1/sd-plans-v1/commissions",
    sdPlanSummaryV1: "/v1/sd-plans-v1/plan-summary",
    sdPolicySummaryV1: "/v1/sd-plans-v1/policy-summary",
    sdFinancingSummaryV1: "/v1/sd-plans-v1/financing-summary",
    sdInvoiceNotifications: "/v1/sd-plans-v1/invoice-notifications",
    sdPlanReviewCentre: "/v1/sd-plans-v1/plan-review-centre",
    sdPlanReviewV1: "/v1/sd-plans-v1/plan-review",
    sdReviewCenterCommonDataV1: "/v1/sd-plans-v1/review-center-common-data",
    pfProposalsPendingFinancingReject: "/proposals-pending/reject",
    pfProposalsPendingFinancingApprove: "/proposals-pending/approve",
    pfProposalsPendingBasicInfoGenerateInvoice: "/pf-proposals-pending/basic-info/generate-invoice",
    createPlanDocs: "/pf-proposals-pending/upload/plan-docs",
    uploadDeleteGetPlanDoc: "/pf-proposals-pending/plan-docs",
    pfProposalsPendingFinancingGenerateInvoice: "/pf-proposals-pending/financing/generate-invoice",
    pfProposalsPendingInsuranceGenerateInvoice:
      "/pf-proposals-pending/insurance-underwriting/generate-invoice",
    pfProposalsPendingDeleteInvoice: "/pf-proposals-pending/invoice",
    pfInsureddList: "/pf-proposals-pending/insureds-list",
    pfProposalsPendingCreatePlan: "/pf-proposals-pending/create-plan",
    pfPlanSummary: "/pf-plans/plan-summary",
    pfPolicySummary: "/pf-plans/policy-summary",
    pfFinancingSummary: "/pf-plans/financing-summary",
    pfInvoiceNotifications: "/pf-plans/invoice-notifications",
    reviewCenterTimeline: "/pf-plans/review-center/timeline",
    reviewCenterReview: "/pf-plans/review-center",
    bsReviewCenterReview: "/v1/bs-plans-v1/review-center",
    bsShareholderList: "/v1/bs-plans-v1/shareholder-list",
    bsValueReviewValuation: "/v1/bs-plans-v1/review-center/value-review",
    bsBusinessValuation: "/v1/bs-plans-v1/review-center/business-valuation",
    reviewCenterReviewYears: "/pf-plans/review-center/review-years",
    reviewCenterProgress: "/pf-plans/review-center/progress-sections",
    reviewCenterPlanReview: "/pf-plans/review-center/plan-review",
    bsReviewCenterPlanReview: "/v1/bs-plans-v1/review-center/plan-review",
    reviewCenterPolicyReview: "/pf-plans/review-center/policy-review",
    bsReviewCenterPolicyReview: "/v1/bs-plans-v1/review-center/policy-review",
    reviewCenterFinancingReview: "/pf-plans/review-center/financing-review",
    reviewCenterWaiveOffLateFeeInterestPayment:
      "/pf-plans/waive-off-late-fee/interest-payment-invoice",
    reviewCenterWaiveOffLateFeePrincipalPayment:
      "/pf-plans/waive-off-late-fee/principal-payment-invoice",
    reviewCenterWaiveOffLateFeeOwnerPremium: "/pf-plans/waive-off-late-fee/client-premium-invoice",
    reviewCenterWaiveOffLateFeeLenderPremium: "/pf-plans/waive-off-late-fee/lender-premium-invoice",
    reviewCenterWaiveOffLateFeePlanFees: "/pf-plans/waive-off-late-fee/plan-fees-invoice",
    reviewCenterPolicyInsuredList: "/pf-plans/policy-insured-list",
    pfToggleLockPlanReview: "/pf-plans/toggle-lock-plan-review",
    pfProposalsPendingInsuranceUnderwriting: "/pf-proposals-pending/insurance-underwriting",
    masterDataLenders: "/super-admin/master-data/lenders",
    reviewCenterFinancingDetails: "/pf-plans/review-center/financing-detail",
    reviewCenterValueDetails: "/v1/bs-plans-v1/review-center/shareholder-ledger",
    bsShareholderLedgerOwnership: "/v1/bs-plans-v1/review-center/shareholder-ledger",
    bsV1Shareholder: "/v1/bs-plans-v1/review-center/shareholder",
    reviewCenterFinancingOutsideAssetsLedger:
      "/pf-plans/review-center/financing-outside-assets-ledger",
    reviewCenterPolicyDetail: "/pf-plans/review-center/policy-detail",
    bsReviewCenterPolicyDetail: "/v1/bs-plans-v1/review-center/policy-detail",
    bsReviewCenterPlanPolicy: "/v1/bs-plans-v1/review-center/plan",
    reviewCenterPlanDetails: "/pf-plans/review-center/plan-detail",
    sdPolicyReviewDetailV1: "/v1/sd-plans-v1/policy-review-detail",
    sdPolicyReviewV1: "/v1/sd-plans-v1/policy-review",
    sdFinancingReviewV1: "/v1/sd-plans-v1/financing-review",
    sdFinancingReviewDetailV1: "/v1/sd-plans-v1/financing-review-detail",
    bsReviewCenterPlanDetails: "/v1/bs-plans-v1/review-center/plan-detail",
    reviewCenterCollateralCheckSnapshot: "/pf-plans/review-center/collateral-check-snapshot",
    reviewCenterPolicyCreditingDelete: "/pf-plans/review-center/policy-crediting",
    sdReviewCenterPolicyCreditingDelete: "/v1/sd-plans-v1/policy-crediting",
    adsActivePfBannerList: "/advertisement/active-pf-banner-list",
    adsActiveSdBannerList: "/advertisement/active-sd-banner-list",
    treasuryVerificationDetails: "/treasury/status",
    treasuryOnboardingLink: "/treasury/onboarding-link",
    treasuryOnboardingSession: "/treasury/onboarding-session",
    createSessionForBankLinking: "/treasury/create-session-for-bank-linking",
    createSessionForBankLinkingStripe: "/stripe-subscription/create-session-for-bank-linking",
    createSessionForCardLinkingStripe: "/stripe-subscription/create-session-for-card-linking",
    getPaymentMethods: "/treasury/payment-methods",
    deletePaymentMethod: "/treasury/payment-method",
    getWalletInfo: "/treasury/wallet-info",
    withdrawFromWallet: "/treasury/withdraw-from-wallet",
    checkWithdrawStatus: "/treasury/check-withdraw-status",
    depositToWallet: "/treasury/deposit-to-wallet",
    setDefaultPaymentMethod: "/treasury/set-default-payment-method",
    completeBankLinking: "/treasury/complete-bank-linking",
    completeBankLinkingStripe: "/stripe-subscription/complete-bank-linking",
    getPaymentTransactions: "/treasury/transactions",
    setConnectedAccount: "/treasury/create-connected-account",
    setConnectedAgencyAccount: "/treasury/create-agency-connected-account",
    setUpdateConnectedAccount: "/treasury/update-ssn-or-id-number",
    entityDetail: "/entity",
    entityCreate: "/entity/create",
    entityUpdate: "/entity/update",
    entityList: "/entity/list",
    entityAllEntities: "/entity/all-entities",
    entityDeactivate: "/entity/deactivate",
    entityContacts: "/entity/entity-contacts",
    entityLinkEntity: "/entity/link-entity",
    entityLink: "/entity/link",
    entityThreads: "/entity/threads",
    entityAdvisors: "/entity/entity-advisor",
    entityAdvisorsAdd: "/entity/entity-advisor/create",

    entityAdvisorsReorder: "/entity/entity-advisor/update-types",
    entityAdvisorsPrimary: "/entity/entity-advisor/primary",
    entityAdvisorRemove: "/entity/entity-advisor/remove",

    contactAdvisors: "/contact",
    contactAdvisorsReorder: "/contact/update-types",
    contactAdvisorsPrimary: "/contact/primary",
    contactContactAdvisorRemove: "/contact/contact-advisor/remove",
    contactContactAdvisorUpdateTypes: "/contact/contact-advisor/update-types",
    agencySubscription: "/agency-subscriptions",
    agencySubscriptionAdvisors: "/agency-subscriptions/advisors",
    agencySubscriptionAdvisorsWithoutSeat: "/agency-subscriptions/advisors-without-seat",
    agencySubscriptionRemoveSeat: "/agency-subscriptions/seats",
    assistantSubscription: "/assistant-subscription",
    assistantSubscriptionPurchase: "/assistant-subscription/purchase",
    assistantSubscriptionCancel: "/assistant-subscription/cancel",
    assistantSubscriptionBillingHistory: "/assistant-subscription/billing-history",
    assistantSubscriptionBillingInformation: "/assistant-subscription/billing-information",
    stripeSubscription: "/stripe-subscription",
    stripeSubscriptionPaymentMethods: "/stripe-subscription/payment-methods",
    stripeSubscriptionSetDefaultPaymentMethod: "/stripe-subscription/set-default-payment-method",
    stripeSubscriptionDeletePaymentMethod: "/stripe-subscription/payment-method",
    stripeSubscriptionProrationDetail: "/stripe-subscription/proration-detail",
    caseManagerAdvisors: "/agents/advisor-admins",
    agencyAssignSeatsBulk: "/agency-subscriptions/seats/assign-bulk",
    agencyAssignSeat: "/agency-subscriptions/seats/assign",
    agencyChangePlan: "/agency-subscriptions/seats/change-plan",
    agencySubscriptionDetails: "/stripe-subscription/proration-detail-for-seat-conversion",
    agencySubscriptionProrationDetailForSeats: "/stripe-subscription/proration-detail-for-seats",
    contactActivity: "/contact/activities",
    pendingActiveAndInactive: "/proposals/list-proposals/approved-pending-and-pending-inactive",
    proposalActiveAndInactive: "/proposals/list-proposals/active-and-inactive",
    seatAllocationRequests: "/stripe-subscription/seat-allocation/requests",
    primaryConstactEntities: "/entity/primary-contact-entities",
    payPlanInvoice: "/treasury/pay-plan-invoice",
    payPendingInvoice: "/treasury/pay-pending-invoice",
    insuranceCompanyAnalyticsTotals: "/analytics/insurance-company/totals",
    insuranceCompanyAnalyticsPolicyTotals: "/analytics/insurance-company/policy-totals",
    settingNotification: "/profile/setting/notification",
    solutionPricing: "/plans/plan-pricing",
    advisorContactAllThreads: "/contact/all/threads",
    bsPolicySummaryV1: "/v1/bs-plans-v1/policy-summary",
    kpiPolicySummaryV1: "/v1/kpi-plans-v1/policy-summary",
    kpiReviewCenterEmployeeList: "/v1/kpi-plans-v1/review-center/employee-list",
    sdProposalDetails: "/v1/sd-proposals-v1/proposal-details",
    sdOtherDetails: "/v1/sd-proposals-v1/other-details",
    sdAddPolicyInput: "/v1/sd-proposals-v1/add-policy-input",
    sdPlanAdminSplitDollar: "/v1/sd-proposals-v1/plan-admin-split-dollar",
    sdPlanSplitDollar: "/v1/sd-proposals-v1/plan-split-dollar",
    sdSaveProposalPlan: "/v1/sd-proposals-v1/plan-data-save",

    // BS Proposals Pending V1
    bsPendingV1BasicInfo: "/v1/bs-proposals-pending-v1/basic-info",
    bsPendingV1InsuredEntities: "/v1/bs-proposals-pending-v1/insured-entities",
    bsPendingV1InsuranceList: "/v1/bs-proposals-pending-v1/insurance-underwriting/list",
    /** GET …/insured-info/:proposalId/:shareholderId */
    bsPendingV1InsuredInfo: "/v1/bs-proposals-pending-v1/insured-info",
    bsPendingV1CoverageRequest: "/v1/bs-proposals-pending-v1/coverage-request",
    bsPendingV1Legal: "/v1/bs-proposals-pending-v1/legal",
    bsPendingV1Finalization: "/v1/bs-proposals-pending-v1/finalization",
    bsPendingV1CreatePlan: "/v1/bs-proposals-pending-v1/create-plan",
    bsPendingV1SaveDetails: "/v1/bs-proposals-pending-v1/save-details",
    bsPendingV1UploadDocument: "/v1/bs-proposals-pending-v1/upload/proposal-document",
    bsPendingV1ApproveDoc: "/v1/bs-proposals-pending-v1/approve",
    bsPendingV1RejectDoc: "/v1/bs-proposals-pending-v1/reject",
    bsPendingV1DeleteDoc: "/v1/bs-proposals-pending-v1/proposal-document",
    bsPendingV1InvoicesList: "/v1/bs-proposals-pending-v1/invoices-list",
    bsPendingV1GenerateInvoice: "/v1/bs-proposals-pending-v1/basic-info/generate-invoice",
    bsPendingV1DeleteInvoice: "/v1/bs-proposals-pending-v1/basic-info/invoice",
    bsPendingV1AdditionalReqApprove: "/v1/bs-proposals-pending-v1/additional-requirements/approve",
    bsPendingV1AdditionalReqReject: "/v1/bs-proposals-pending-v1/additional-requirements/reject",
    bsPendingV1AdditionalReq: "/v1/bs-proposals-pending-v1/additional-requirements",
    bsPendingV1UploadAdditionalReq: "/v1/bs-proposals-pending-v1/upload",
    bsPendingV1InsuranceUploadDoc:
      "/v1/bs-proposals-pending-v1/insurance-underwriting/upload/proposal-document",
    bsPendingV1InsuranceApproveDoc: "/v1/bs-proposals-pending-v1/insurance-underwriting/approve",
    bsPendingV1InsuranceRejectDoc: "/v1/bs-proposals-pending-v1/insurance-underwriting/reject",
    bsPendingV1InsuranceDeleteDoc:
      "/v1/bs-proposals-pending-v1/insurance-underwriting/proposal-document",
    bsPendingV1LegalSaveDetails: "/v1/bs-proposals-pending-v1/legal/save-details",
    bsPendingV1LegalUploadDoc: "/v1/bs-proposals-pending-v1/legal/upload/proposal-document",
    bsPendingV1LegalApproveDoc: "/v1/bs-proposals-pending-v1/legal/approve",
    bsPendingV1LegalRejectDoc: "/v1/bs-proposals-pending-v1/legal/reject",
    bsPendingV1LegalDeleteDoc: "/v1/bs-proposals-pending-v1/legal/proposal-document",
    bsPendingV1FinalizationSaveDetails: "/v1/bs-proposals-pending-v1/finalization/save-details",
    bsPendingV1FinalizationUploadDoc:
      "/v1/bs-proposals-pending-v1/finalization/upload/proposal-document",
    bsPendingV1FinalizationApproveDoc: "/v1/bs-proposals-pending-v1/finalization/approve",
    bsPendingV1FinalizationRejectDoc: "/v1/bs-proposals-pending-v1/finalization/reject",
    bsPendingV1FinalizationDeleteDoc: "/v1/bs-proposals-pending-v1/finalization/proposal-document",
    bsPendingV1InsuranceLIGenerateInvoice:
      "/v1/bs-proposals-pending-v1/life-insurance-underwriting/generate-invoice",
    bsPendingV1InsuranceDIGenerateInvoice:
      "/v1/bs-proposals-pending-v1/disability-insurance-underwriting/generate-invoice",
    bsPendingV1InsuranceLIDeleteInvoice:
      "/v1/bs-proposals-pending-v1/life-insurance-underwriting/delete-invoice",
    bsPendingV1InsuranceDIDeleteInvoice:
      "/v1/bs-proposals-pending-v1/disability-insurance-underwriting/delete-invoice",
    bsPendingV1InsuranceAdditionalReqApprove:
      "/v1/bs-proposals-pending-v1/insurance-additional-requirements/approve",
    bsPendingV1InsuranceAdditionalReqReject:
      "/v1/bs-proposals-pending-v1/insurance-additional-requirements/reject",
    bsPendingV1InsuranceAdditionalReq:
      "/v1/bs-proposals-pending-v1/insurance-additional-requirements",
    bsPendingV1InsuranceUploadAdditionalReq: "/v1/bs-proposals-pending-v1/insurance-upload",
    bsPendingV1LifeInsurance: "/v1/bs-proposals-pending-v1/life-insurance-underwriting",
    bsPendingV1DisabilityInsurance: "/v1/bs-proposals-pending-v1/disability-insurance-underwriting",
    bsPendingV1LifeInsuranceSaveDetails:
      "/v1/bs-proposals-pending-v1/life-insurance-underwriting/save-details",
    bsPendingV1DisabilityInsuranceSaveDetails:
      "/v1/bs-proposals-pending-v1/disability-insurance-underwriting/save-details",
    bsPendingV1InsuredInfoSaveDetails: "/v1/bs-proposals-pending-v1/insured-info/save-details",
    bsPendingV1InsuredInfoUploadDoc:
      "/v1/bs-proposals-pending-v1/insured-info/upload/proposal-document",
    bsPendingV1InsuredInfoApproveDoc: "/v1/bs-proposals-pending-v1/insured-info/approve",
    bsPendingV1InsuredInfoRejectDoc: "/v1/bs-proposals-pending-v1/insured-info/reject",
    bsPendingV1InsuredInfoDeleteDoc: "/v1/bs-proposals-pending-v1/insured-info/proposal-document",

    // KP Proposal Pending V1
    kpiProposalsPendingV1SaveDetails: "/v1/kpi-proposals-pending-v1/save-details",
    kpiProposalsPendingV1BasicInfo: "/v1/kpi-proposals-pending-v1/basic-info",
    kpiProposalsPendingV1InsuranceEmployees:
      "/v1/kpi-proposals-pending-v1/insurance-underwriting/employees",
    kpiProposalsPendingV1LifeInsuranceUnderwriting:
      "/v1/kpi-proposals-pending-v1/insurance-underwriting/life",
    kpiProposalsPendingV1DisabilityInsuranceUnderwriting:
      "/v1/kpi-proposals-pending-v1/insurance-underwriting/disability",
    kpiProposalsPendingV1InsuredInfo:
      "/v1/kpi-proposals-pending-v1/insurance-underwriting/insured-info",
    kpiProposalsPendingV1LegalDetails: "/v1/kpi-proposals-pending-v1/legal",
    kpiProposalsPendingV1Finalization: "/v1/kpi-proposals-pending-v1/finalization",
    kpiProposalsPendingV1GenerateBasicInvoice:
      "/v1/kpi-proposals-pending-v1/basic-info/generate-invoice",
    kpiProposalsPendingV1GenerateLifeInvoice:
      "/v1/kpi-proposals-pending-v1/insurance-underwriting/life/generate-invoice",
    kpiProposalsPendingV1GenerateDisabilityInvoice:
      "/v1/kpi-proposals-pending-v1/insurance-underwriting/disability/generate-invoice",
    kpiProposalsPendingV1DeleteInvoice: "/v1/kpi-proposals-pending-v1/invoice",
    kpiProposalsPendingV1InvoicesList: "/v1/kpi-proposals-pending-v1/invoices-list",
    kpiProposalsPendingV1UploadDocument: "/v1/kpi-proposals-pending-v1/upload/proposal-document",
    kpiProposalsPendingV1DeleteDocument: "/v1/kpi-proposals-pending-v1/proposal-document",
    kpiProposalsPendingV1UploadAdditionalReq: "/v1/kpi-proposals-pending-v1/upload",
    kpiProposalsPendingV1UpdateAdditionalReq:
      "/v1/kpi-proposals-pending-v1/additional-requirements",
    kpiProposalsPendingV1ApproveAdditionalReq:
      "/v1/kpi-proposals-pending-v1/additional-requirements/approve",
    kpiProposalsPendingV1RejectAdditionalReq:
      "/v1/kpi-proposals-pending-v1/additional-requirements/reject",
    kpiProposalsPendingV1PlanDocs: "/v1/kpi-proposals-pending-v1/plan-docs",
    kpiProposalsPendingV1UploadPlanDoc: "/v1/kpi-proposals-pending-v1/upload/plan-docs",
    kpiProposalsPendingV1DeletePlanDoc: "/v1/kpi-proposals-pending-v1/plan-docs",
    kpiProposalsPendingV1ApproveDoc: "/kpi-proposals-pending/approve",
    kpiProposalsPendingV1RejectDoc: "/kpi-proposals-pending/reject",
    kpiProposalsPendingV1ApproveOriginalProposal:
      "/kpi-proposals-pending/plan-finalization/approve-original-proposal",
    kpiProposalsPendingV1CreatePlan: "/v1/kpi-proposals-pending-v1/create-plan",

    // KP review center V1
    kpiV1ReviewCenter: "/v1/kpi-plans-v1/review-center",
    kpiV1PlanDetail: "/v1/kpi-plans-v1/review-center/plan-detail",
    kpiV1KeyPersonLedger: "/v1/kpi-plans-v1/review-center/key-person-ledger",
    kpiV1KeyPerson: "/v1/kpi-plans-v1/review-center/key-person",
    kpiV1YearlyKeyPersonLedger: "/v1/kpi-plans-v1/review-center/yearly-key-person-ledger",
    kpiV1PolicyDetail: "/v1/kpi-plans-v1/review-center/policy-details",
    kpiV1ContactList: "/v1/kpi-plans-v1/review-center/contact-list",
    kpiV1PolicyReview: "/v1/kpi-plans-v1/review-center/policy-review",
    kpiV1PlanReview: "/v1/kpi-plans-v1/review-center/plan-review",
    kpiV1CommissionTracking: "/v1/kpi-plans-v1/commissions",
    addAgentKpiV1CommissionTracking: "/v1/kpi-plans-v1/commissions/add-agent",
    removeAgentKpiV1CommissionTracking: "/v1/kpi-plans-v1/commissions/remove-agent",
    updateAgentKpiV1CommissionTracking: "/v1/kpi-plans-v1/commissions/update-agent",
    kpiV1LifePolicy: "/v1/kpi-plans-v1/life-policy",
    kpiV1DisabilityPolicy: "/v1/kpi-plans-v1/disability-policy",
    bsV1LifePolicy: "/v1/bs-plans-v1/life-policy",
    bsV1DisabilityPolicy: "/v1/bs-plans-v1/disability-policy",
    creditLedgerByAgent: "/credit-ledger/agent-credit-ledger",
    assistantAccessList: "/assistant-access/list",
    assistantAccessAction: "/assistant-access/action",
    assistantAccessRequest: "/assistant-access/request",
    assistantAccessActivity: "/assistant-access",
  },
  errorCode: [400, 403, 401, 412, 500, 405],
  successCode: [200, 201, 202, 203, 204, 205, 206, 207],
  excludeToken: [
    "/auth/signup",
    "/auth/signin",
    "/auth/verifyToken",
    "/auth/refresh",
    "/auth/check-session",
    "/auth/check-credentials",
  ],
};

export default apiConfig;
